import React, { Component, ComponentType } from "react";
import { Route, Switch } from "react-router-dom";
import { connect } from "react-redux";
import "@js-joda/timezone";
import { withLDProvider } from "launchdarkly-react-client-sdk";

import Loading from "./components/Loading";
import Notification from "./components/Notification";
import Home from "./views/Home";
import EnterCode from "./views/EnterCode";
import ResidentDirectory from "./views/ResidentDirectory";
import ScanQR from "./views/ScanQR";
import Call from "./views/Call";
import {
  enqueueAccessRequest,
  getPropertyData,
  handleCheckin,
  handleWiegandNotification,
  openGate,
  persistLog,
  sendNewAccessRequestsIfOnline,
} from "./redux/actions/property";
import { handleDeviceOffline, handleDeviceOnline } from "./redux/actions/app";
import pusher from "./common/lib/pusher";
import rawr from "./common/lib/rawr";
import {
  AccessRequestSource,
  Device,
  Notification as TNotification,
  Property,
  PropertyStaffUser,
  Resident,
} from "src/common/types";
import { RootState, TypedDispatch } from "src";
import config from "src/common/config";
import { GateScheduleWall } from "src/views/GateScheduleWall";

const PROPERTY_REFRESH_INTERVAL = 60000; // 1 minute
const ERROR_REFRESH_INTERVAL = 5000; // 5 seconds
const CHECKIN_INTERVAL = 30000; // 30 seconds
const SEND_ACCESS_REQUESTS_INTERVAL = 30000; // 30 seconds

type AppProps = {
  dispatch: TypedDispatch;
  property: Property | null;
  propertyDataError: boolean;
  areAccessRequestsSending: boolean;
  device: Device;
  propertyDataLoaded: boolean;
  notifications: TNotification[];
  residents: Resident[];
  staff: PropertyStaffUser[];
};

type AppState = {
  isGateOpenFromSchedule: boolean;
};

class App extends Component<AppProps, AppState> {
  propertyRefreshInterval: number;
  checkinInterval: number;
  sendAccessRequestsInterval: number;
  scheduleInterval: number;

  constructor(props: AppProps) {
    super(props);

    this.state = {
      isGateOpenFromSchedule: false,
    };

    this.propertyRefreshInterval = 0;
    this.checkinInterval = 0;
    this.sendAccessRequestsInterval = 0;
    this.scheduleInterval = 0;
  }

  componentDidMount() {
    const { dispatch, property } = this.props;

    dispatch(getPropertyData());
    dispatch(sendNewAccessRequestsIfOnline());

    this.propertyRefreshInterval = window.setInterval(
      () => dispatch(getPropertyData()),
      PROPERTY_REFRESH_INTERVAL
    );
    this.checkinInterval = window.setInterval(
      this.handleCheckin,
      CHECKIN_INTERVAL
    );
    this.sendAccessRequestsInterval = window.setInterval(
      this.sendNewAccessRequests,
      SEND_ACCESS_REQUESTS_INTERVAL
    );

    window.addEventListener("online", this.handleNetworkChange);
    window.addEventListener("offline", this.handleNetworkChange);

    this.bindRawr();

    if (property) {
      this.bindPusher();
    }
  }

  componentDidUpdate(prevProps: AppProps) {
    const { property, propertyDataError, dispatch } = this.props;

    if (
      property &&
      (!prevProps.property || prevProps.property.id !== property.id)
    ) {
      if (prevProps.property) {
        this.unbindPusher(prevProps.property, prevProps.device);
      }

      this.bindPusher();
    }

    if (!prevProps.propertyDataError && propertyDataError) {
      window.clearInterval(this.propertyRefreshInterval);
      this.propertyRefreshInterval = window.setInterval(
        () => dispatch(getPropertyData()),
        ERROR_REFRESH_INTERVAL
      );
    }

    if (prevProps.propertyDataError && !propertyDataError) {
      window.clearInterval(this.propertyRefreshInterval);
      this.propertyRefreshInterval = window.setInterval(
        () => dispatch(getPropertyData()),
        PROPERTY_REFRESH_INTERVAL
      );
    }
  }

  componentWillUnmount() {
    window.clearInterval(this.propertyRefreshInterval);
    window.clearInterval(this.checkinInterval);
    window.clearInterval(this.sendAccessRequestsInterval);

    window.removeEventListener("online", this.handleNetworkChange);
    window.removeEventListener("offline", this.handleNetworkChange);
  }

  handleCheckin = () => {
    const { property, dispatch } = this.props;

    if (property) {
      dispatch(handleCheckin());
    }
  };

  sendNewAccessRequests = () => {
    const { areAccessRequestsSending, dispatch } = this.props;
    if (!areAccessRequestsSending) {
      dispatch(sendNewAccessRequestsIfOnline());
    }
  };

  handleNetworkChange = (e: Event) => {
    const { dispatch } = this.props;

    if (navigator.onLine) {
      dispatch(handleDeviceOnline());
    } else {
      dispatch(handleDeviceOffline());
    }
  };

  unbindPusher = (property: Property, device: Device) => {
    pusher.unsubscribe(`private-telephone-entry-property-${property.id}`);
    pusher.unsubscribe(`private-telephone-entry-intercom-${device.id}`);
  };

  bindPusher = () => {
    const { property, dispatch, device } = this.props;

    if (property) {
      this.unbindPusher(property, device);
      const channel = pusher.subscribe(
        `private-telephone-entry-property-${property.id}`
      );

      channel.bind("pusher:subscription_error", () => {
        setTimeout(() => {
          channel.subscribe();
        }, ERROR_REFRESH_INTERVAL);
      });

      channel.bind("property-updated", () => {
        dispatch(getPropertyData());
      });

      const intercomChannel = pusher.subscribe(
        `private-telephone-entry-intercom-${device.id}`
      );

      intercomChannel.bind("pusher:subscription_error", () => {
        setTimeout(() => {
          intercomChannel.subscribe();
        }, ERROR_REFRESH_INTERVAL);
      });

      intercomChannel.bind("open-gate", (data: any) => {
        let foundCredential;
        if (data.tenantId) {
          const foundTenant = this.props.residents.find(
            (resident) => resident.id === data.tenantId
          );
          if (!foundTenant) {
            dispatch(
              persistLog("DMI : Access granted by unknown tenant, ignoring", {
                tenantId: data.tenantId,
              })
            );
            return;
          }
          if (!foundTenant.property_access_credential) {
            dispatch(
              persistLog(
                "DMI : Access granted by tenant with no credentials, ignoring",
                {
                  tenantId: data.tenantId,
                }
              )
            );
            return;
          }
          foundCredential = foundTenant.property_access_credential;
        } else {
          const managerWithCode = this.props.staff.find(
            (staff: PropertyStaffUser) => staff.property_credentials.length > 0
          );
          if (!managerWithCode) {
            return;
          }
          foundCredential = managerWithCode.property_credentials[0];
        }

        if (!foundCredential.active) {
          dispatch(
            persistLog(
              "DMI : Access granted by user with inactive credential, ignoring",
              {
                tenantId: data.tenantId,
              }
            )
          );
          return;
        }
        dispatch(openGate(foundCredential.code));
        dispatch(
          enqueueAccessRequest(
            foundCredential.code,
            true,
            AccessRequestSource.MobileIntercom
          )
        );
      });
    }
  };

  bindRawr = () => {
    const { dispatch } = this.props;

    // A user code was received from the hardware (e.g. prox card tapped to reader)
    rawr.notifications.onfacility((facilityId: any, id: any) =>
      dispatch(handleWiegandNotification(id))
    );

    rawr.notifications.onrefresh((facilityId: any, id: any) => {
      console.log("rawr : onrefresh", facilityId, id);
      window.location.reload();
    });
  };

  render() {
    const { propertyDataLoaded, propertyDataError, property, notifications } =
      this.props;

    if (!propertyDataLoaded) {
      return <Loading />;
    }

    if (propertyDataError && !property) {
      return (
        <Notification
          status="ERROR"
          title="Could not load information about the Property"
          body="Please check that there is a valid token in the URL"
        />
      );
    }

    return (
      <div className="background">
        {notifications[0] && (
          <Notification
            status={notifications[0].status}
            title={notifications[0].title}
            body={notifications[0].body}
          />
        )}
        <GateScheduleWall>
          <Switch>
            <Route exact path="/" component={Home} />
            <Route exact path="/call" component={Call} />
            <Route exact path="/enter-code" component={EnterCode} />
            <Route
              exact
              path="/resident-directory"
              component={ResidentDirectory}
            />
            <Route exact path="/scan-qr" component={ScanQR} />
          </Switch>
        </GateScheduleWall>
      </div>
    );
  }
}

const ConnectedApp = connect((state: RootState) => ({
  areAccessRequestsSending: state.property.areAccessRequestsSending,
  propertyDataError: state.property.error,
  propertyDataLoaded: state.property.loaded,
  device: state.property.device,
  property: state.property.meta,
  notifications: state.app.notifications,
  residents: state.property.residents,
  staff: state.property.staff,
}))(App);

export default withLDProvider({
  clientSideID: config.launchDarklyKey,
  options: {
    streaming: false,
  },
})(ConnectedApp as ComponentType);
